// ─── ADMIN DELIVERY LOGS ───────────────────────────────────────
const DELIVERY_PROVIDER_LABELS = {
  csv: 'CSV',
  sftp: 'SFTP',
  dcix: 'DCIx',
  shipbob: 'ShipBob',
  gmail: 'Gmail',
};

const DELIVERY_STATUS_META = {
  success: { label:'Success', color:'#166534', bg:'#DCFCE7', Icon: CheckCircle2 },
  error: { label:'Error', color:'#991B1B', bg:'#FEE2E2', Icon: XCircle },
  skipped: { label:'Skipped', color:'#374151', bg:'#F3F4F6', Icon: Clock3 },
};

const DeliveryLogsTableScreen = ({ adminMode = false }) => {
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState([]);
  const [provider, setProvider] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError('');
    const query = new URLSearchParams();
    if (provider) query.set('provider', provider);
    if (status) query.set('status', status);
    const endpoint = adminMode ? '/api/admin/fulfillment-logs' : '/api/fulfillment-logs';
    const { ok, data } = await api('GET', `${endpoint}${query.toString() ? `?${query}` : ''}`);
    setLoading(false);
    if (!ok) {
      setError(data?.error || 'Could not load delivery logs.');
      return;
    }
    setLogs(data.logs || []);
    setSummary(data.summary || []);
  }, [adminMode, provider, status]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  const formatTime = value => {
    if (!value) return 'Unknown';
    const date = new Date(`${value}Z`);
    return Number.isNaN(date.getTime()) ? value : date.toLocaleString([], { dateStyle:'medium', timeStyle:'short' });
  };

  const statusBadge = value => {
    const meta = DELIVERY_STATUS_META[value] || DELIVERY_STATUS_META.skipped;
    const Icon = meta.Icon;
    return <span style={{ display:'inline-flex', alignItems:'center', gap:5, padding:'3px 7px', borderRadius:5, background:meta.bg, color:meta.color, fontSize:11, fontWeight:900 }}><Icon size={12}/>{meta.label}</span>;
  };

  const countFor = (nextProvider, nextStatus) => summary.find(row => row.provider === nextProvider && row.status === nextStatus)?.count || 0;
  const total30 = summary.reduce((sum, row) => sum + Number(row.count || 0), 0);
  const tableHeaders = adminMode
    ? ['Time', 'Status', 'Provider', 'Destination', 'Account', 'Orders', 'Reference', 'Message']
    : ['Time', 'Status', 'Provider', 'Destination', 'Orders', 'Reference', 'Message'];

  return (
    <div className="dash-page-scroll workspace-page">
      <div className="dash-panel workspace-panel-flat" style={{ marginBottom:14 }}>
        <div className="dash-panel-header" style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:16 }}>
          <div style={{ minWidth:0, flex:1 }}>
            <h1 className="dash-page-title">{adminMode ? 'Delivery Logs' : 'Send Activity'}</h1>
            <p className="dash-page-subtitle" style={{ maxWidth:760 }}>
              {adminMode
                ? 'Admin view of export and warehouse delivery activity. Secrets and raw API payloads are never shown here.'
                : 'History of CSV generation, SFTP uploads, and API deliveries for your fulfillment destinations.'}
            </p>
          </div>
          <button onClick={loadLogs} disabled={loading} style={{ marginLeft:'auto', flexShrink:0, background:loading ? '#9CA3AF' : '#006FDD', color:'#fff', border:'none', borderRadius:8, padding:'9px 12px', fontSize:12, fontWeight:900, cursor:loading ? 'wait' : 'pointer', display:'flex', alignItems:'center', gap:7 }}>
            <RefreshCw size={14}/>{loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:10, marginBottom:14 }}>
        <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, padding:13 }}>
          <div style={{ fontSize:11, color:'#6B7280', fontWeight:900 }}>LAST 30 DAYS</div>
          <div style={{ fontSize:24, fontWeight:900, color:'#1A1A2E', marginTop:5 }}>{total30}</div>
        </div>
        {['dcix', 'shipbob', 'sftp', 'gmail', 'csv'].map(key => (
          <div key={key} style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:8, padding:13 }}>
            <div style={{ fontSize:11, color:'#6B7280', fontWeight:900 }}>{DELIVERY_PROVIDER_LABELS[key]}</div>
            <div style={{ display:'flex', gap:10, marginTop:8, fontSize:12, color:'#374151', flexWrap:'wrap' }}>
              <span>{countFor(key, 'success')} success</span>
              <span>{countFor(key, 'error')} error</span>
              <span>{countFor(key, 'skipped')} skipped</span>
            </div>
          </div>
        ))}
      </div>

      <div className="dash-panel workspace-panel-flat" style={{ overflow:'hidden' }}>
        <div style={{ padding:'12px 14px', borderBottom:'1px solid #F3F4F6', display:'flex', gap:10, justifyContent:'space-between', alignItems:'center', flexWrap:'wrap' }}>
          <div style={{ fontSize:13, fontWeight:900 }}>Recent activity</div>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            <select value={provider} onChange={e => setProvider(e.target.value)} style={{ border:'1px solid #E5E7EB', borderRadius:7, padding:'7px 9px', fontSize:12, outline:'none', background:'#fff' }}>
              <option value="">All providers</option>
              <option value="dcix">DCIx</option>
              <option value="sftp">SFTP</option>
              <option value="csv">CSV</option>
              <option value="gmail">Gmail</option>
              <option value="shipbob">ShipBob</option>
            </select>
            <select value={status} onChange={e => setStatus(e.target.value)} style={{ border:'1px solid #E5E7EB', borderRadius:7, padding:'7px 9px', fontSize:12, outline:'none', background:'#fff' }}>
              <option value="">All statuses</option>
              <option value="success">Success</option>
              <option value="error">Error</option>
              <option value="skipped">Skipped</option>
            </select>
          </div>
        </div>

        {error && <div style={{ padding:'10px 14px', borderBottom:'1px solid #FEE2E2', background:'#FEF2F2', color:'#991B1B', fontSize:12, fontWeight:800 }}>{error}</div>}

        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', minWidth:900, borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:'#F9FAFB', borderBottom:'1px solid #E5E7EB' }}>
                {tableHeaders.map(header => (
                  <th key={header} style={{ textAlign:'left', padding:'10px 12px', color:'#6B7280', fontSize:11, fontWeight:900 }}>{header}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={tableHeaders.length} style={{ padding:18, color:'#9CA3AF' }}>Loading delivery logs...</td></tr>
              ) : logs.length === 0 ? (
                <tr><td colSpan={tableHeaders.length} style={{ padding:18, color:'#9CA3AF' }}>No delivery logs yet.</td></tr>
              ) : logs.map(log => (
                <tr key={log.id} style={{ borderBottom:'1px solid #F3F4F6' }}>
                  <td style={{ padding:'10px 12px', color:'#374151', whiteSpace:'nowrap' }}>{formatTime(log.createdAt)}</td>
                  <td style={{ padding:'10px 12px' }}>{statusBadge(log.status)}</td>
                  <td style={{ padding:'10px 12px', fontWeight:900, color:'#1A1A2E' }}>{DELIVERY_PROVIDER_LABELS[log.provider] || log.provider}</td>
                  <td style={{ padding:'10px 12px', color:'#374151', maxWidth:190, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={log.ruleName}>{log.ruleName}</td>
                  {adminMode && <td style={{ padding:'10px 12px', color:'#6B7280', maxWidth:210, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={log.user?.email}>{log.user?.email || '-'}</td>}
                  <td style={{ padding:'10px 12px', color:'#374151' }}>
                    <span style={{ fontWeight:900 }}>{log.rowCount || 0}</span>
                    {log.orderIds?.length > 0 && <span title={log.orderIds.join(', ')} style={{ marginLeft:6, color:'#9CA3AF' }}>({log.orderIds.slice(0, 2).join(', ')}{log.orderIds.length > 2 ? ` +${log.orderIds.length - 2}` : ''})</span>}
                  </td>
                  <td style={{ padding:'10px 12px', color:'#6B7280', fontFamily:'monospace', fontSize:11, maxWidth:180, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={log.externalRef}>{log.externalRef || '-'}</td>
                  <td style={{ padding:'10px 12px', color:log.status === 'error' ? '#991B1B' : '#6B7280', maxWidth:260, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }} title={log.message}>{log.message || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const AdminDeliveryLogsScreen = () => <DeliveryLogsTableScreen adminMode={true}/>;
